import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminWallMaterial,
  InsertWallMaterialBody,
  UpdateWallMaterialNewImageBody,
  UpdateWallMaterialRemoveImageBody,
} from "./types/wallMaterials";

export const useWallMaterialsApi = () => {
  const api = useApi();

  const getAdminWallMaterial = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/vertigrip/wall_materials`;
      return useSWRMutation<AdminWallMaterial, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminWallMaterial>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminInstallationTypeWallMaterials = useCallback(
    (installationTypeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/installation_types/${installationTypeId}/wall_materials`, query);
      return useSWR<Paginated<AdminWallMaterial>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminWallMaterial>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminWallMaterial = useCallback(
    async (body: InsertWallMaterialBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/wall_materials`;
      const id = await api.post<{ id: string }, InsertWallMaterialBody>(url, body);
      mutateCache([url, `/admin/settings/vertigrip/installation_types/${body.installationTypeId}/wall_materials`]);
      return id;
    },
    [api],
  );

  const updateAdminWallMaterial = useCallback(
    async (
      wallMaterialId: string,
      body: UpdateWallMaterialRemoveImageBody | UpdateWallMaterialNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_materials/${wallMaterialId}`;
      await api.put<UpdateWallMaterialRemoveImageBody | UpdateWallMaterialNewImageBody>(url, body);
      mutateCache([
        `/admin/settings/vertigrip/wall_materials`,
        `/admin/settings/vertigrip/installation_types/${body.installationTypeId}/wall_materials`,
      ]);
    },
    [api],
  );

  const deleteAdminWallMaterial = useCallback(
    async (wallMaterialId: string, installationTypeId: string): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_materials/${wallMaterialId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/vertigrip/wall_materials`,
        `/admin/settings/vertigrip/wall_materials/${wallMaterialId}`,
        `/admin/settings/vertigrip/installation_types/${installationTypeId}/wall_materials`,
        `/admin/settings/vertigrip/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}`,
      ]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (wallMaterialId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/vertigrip/wall_materials/${wallMaterialId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminWallMaterialVisibility = useCallback(
    async (
      wallMaterialId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { installationTypeId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_materials/${wallMaterialId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/vertigrip/wall_materials",
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/wall_materials`,
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/wall_materials`,
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/wall_materials/${wallMaterialId}`,
      ]);
    },
    [api],
  );

  const updateAdminWallMaterialOrder = useCallback(
    async (
      wallMaterialId: string,
      order: UpdateOrderRequestBody,
      options?: { installationTypeId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_materials/${wallMaterialId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        "/admin/settings/vertigrip/wall_materials",
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/wall_materials`,
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/wall_materials/${wallMaterialId}`,
      ]);
    },
    [api],
  );

  return {
    getAdminWallMaterial,
    useAdminInstallationTypeWallMaterials,
    insertAdminWallMaterial,
    updateAdminWallMaterial,
    updateAdminWallMaterialVisibility,
    updateAdminWallMaterialOrder,
    deleteAdminWallMaterial,
    generateUploadImageUrl,
  };
};
